import { useState } from 'react';

import { useRutilusQuery } from '@graphql/client/useRutilusQuery';
import { SingleSpeciesCard } from '../../../../../../components/Cards/SingleSpeciesCard/SingleSpeciesCard';
import { CloseIcon } from '../../../../../../components/Icons/CloseIcon';
import { SearchIcon } from '../../../../../../components/Icons/SearchIcon';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner/LoadingSpinner';
import { getNodesFromConnection } from '@graphql/connections/getNodesFromConnection';
import { useAllSpeciesAtLocation } from '@domain/regulations/hooks/useAllSpeciesAtLocation';
import { useRegulationBoundaryAtLocation } from '@domain/regulations/hooks/useRegulationBoundaryAtLocation';
import { useMapLocation } from '@domain/map/useMapLocation';
import { imageHasValidRights } from '@lib/images/imageHasValidRights';
import { FormattedSpecies } from '@domain/regulations/formatAllSpeciesData';
import { isProhibitedLocation } from '@domain/regulations/isProhibitedLocation';
import { GetSpeciesImages } from '../../../../../../generated/graphql';
import { getSpeciesImages } from '@graphql/queries/rutilus/Species';
import { MapDrawerLayout } from '../../MapDrawerLayout';
import { MapDrawerNotification } from '../../MapDrawerNotification/MapDrawerNotification';

import { RegulationViewHeader } from './components/RegulationsViewHeader';

function getImage(fishbrainIds: string[], data?: GetSpeciesImages) {
  if (!data || !fishbrainIds.length) {
    return undefined;
  }

  const nodes = getNodesFromConnection(data.species);
  return nodes.find(n => n._id === fishbrainIds[0]);
}

function getFishbrainSpeciesIds(allSpecies: FormattedSpecies[]): string[] {
  const ids: string[] = [];
  allSpecies.forEach(({ fishbrainIds }) => {
    if (fishbrainIds.length) {
      ids.push(fishbrainIds[0]!);
    }
  });
  return ids;
}

export const RegulationsView = () => {
  const mapLocation = useMapLocation();
  const coordinates = mapLocation?.coordinates;
  const [filterValue, setFilterValue] = useState<string>('');
  const { isLoading: isLoadingSpecies, allSpecies } = useAllSpeciesAtLocation(
    coordinates ?? undefined,
  );
  const {
    name: boundaryName,
    boundaries,
    isLoading: isLoadingBoundaries,
  } = useRegulationBoundaryAtLocation(coordinates);

  const { data } = useRutilusQuery(getSpeciesImages, {
    variables: { ids: getFishbrainSpeciesIds(allSpecies) },
    skip: allSpecies.length === 0,
  });

  if (isLoadingSpecies || isLoadingBoundaries) {
    return (
      <MapDrawerLayout>
        <LoadingSpinner loadingText="Loading regulations in the area..." />
      </MapDrawerLayout>
    );
  }

  const noSpeciesFound: boolean = allSpecies.length === 0;
  const filteredSpecies = allSpecies.filter(({ name }) =>
    name.toLocaleLowerCase().trim().includes(filterValue.toLowerCase().trim()),
  );

  const { isProhibited, message } = isProhibitedLocation(boundaries ?? []);

  return (
    <MapDrawerLayout
      headerContent={
        <RegulationViewHeader
          boundaries={boundaries}
          coordinates={coordinates}
          boundaryName={boundaryName}
          isProhibited={isProhibited}
          message={message}
        />
      }
    >
      {!isProhibited && (
        <>
          {!noSpeciesFound && (
            <div className="input-wrapper bg-sea-foam relative mt-2 mr-4 mb-3 flex h-[38px] w-full max-w-lg items-center rounded-lg">
              <span className="search-icon p-4">
                <SearchIcon width={14} height={14} />
              </span>
              <input
                placeholder="Filter species"
                type="text"
                value={filterValue}
                onChange={e => {
                  setFilterValue(e.target.value);
                }}
              />
              {filterValue && (
                <button
                  type="button"
                  className="absolute right-4"
                  onClick={() => {
                    setFilterValue('');
                  }}
                >
                  <CloseIcon />
                </button>
              )}
            </div>
          )}
          <div className="grid grid-cols-1">
            {noSpeciesFound && (
              <MapDrawerNotification
                className="mt-[100px]"
                title="Fishbrain does not yet have regulations for this location"
                description="Try another area to get more results"
              />
            )}
            {!noSpeciesFound && filteredSpecies.length === 0 && (
              <MapDrawerNotification
                className="mt-[100px]"
                title="No results found"
                description="Try another area or species to get more results"
              />
            )}
            {!noSpeciesFound &&
              filteredSpecies.length > 0 &&
              filteredSpecies.map(({ fishRulesId, name, prohibited, fishbrainIds }) => {
                const isProhibitedSpecies = prohibited === 1;
                const speciesNode = getImage(fishbrainIds, data);

                return (
                  <SingleSpeciesCard
                    className="h-[84px]"
                    imageContainerClassName="h-[60px]"
                    speciesId={fishRulesId}
                    imageSrc={
                      speciesNode &&
                      imageHasValidRights(
                        speciesNode.imageCopyrightStatus,
                        speciesNode.imageCopyrightExpiryDate,
                      )
                        ? speciesNode.image.url
                        : undefined
                    }
                    clickEventSource="rules"
                    key={fishRulesId}
                    name={name}
                    prohibited={isProhibitedSpecies}
                  />
                );
              })}
          </div>
        </>
      )}
    </MapDrawerLayout>
  );
};
